import { useState } from "react";
import {
    Box, TextField, Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TablePagination, CircularProgress, Typography,
    Stack, FormControl, InputLabel, Select, MenuItem, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useSelector } from "react-redux";
import { useLazyGetSentCampaignLogsQuery } from "../../store/services/logs-service";

const SentCampaignsLog = () => {
    const { tenancyId } = useSelector((state) => state.tenancy);
    const [filters, setFilters] = useState({
        userId: '',
        campaignId: '',
        dateFrom: '',
        dateTo: '',
        dateFailed: '',
    });
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [selectedRow, setSelectedRow] = useState(null);
    const [trigger, { data, isLoading, error }] = useLazyGetSentCampaignLogsQuery();

    const handleSearch = (newPage = 0) => {
        setPage(newPage);
        trigger({
            tenancyId,
            ...filters,
            page: newPage,
            pageSize,
        });
    };

    const handlePageChange = (event, newPage) => {
        handleSearch(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        const newSize = parseInt(event.target.value, 10);
        setPageSize(newSize);
        setPage(0);
        trigger({
            tenancyId,
            ...filters,
            page: 0,
            pageSize: newSize,
        });
    };

    const columns = [
        { id: 'Id', label: 'ID', width: 60 },
        { id: 'DateCreated', label: 'Date Created', width: 160, isDate: true },
        { id: 'DateUpdated', label: 'Date Updated', width: 160, isDate: true },
        { id: 'DateSent', label: 'Date Sent', width: 160, isDate: true },
        { id: 'UserId', label: 'User ID', width: 200 },
        { id: 'CampaignId', label: 'Simulation Name', width: 180 },
        { id: 'DateFailed', label: 'Date Failed', width: 160, isDate: true },
        { id: 'LandingPageClicked', label: 'Landing Page Clicked', width: 160, isDate: true },
        { id: 'LinkedUniversityCourseId', label: 'Linked Course ID', width: 180 },
        { id: 'UniqueIdentifier', label: 'Unique ID', width: 180 },
        { id: 'UserAgent', label: 'User Agent', width: 200 },
        { id: 'UserAgentIP', label: 'IP Address', width: 120 },
    ];

    const formatDate = (val) => {
        if (!val) return '-';
        try {
            return new Date(val).toLocaleString();
        } catch {
            return val;
        }
    };

    return (
        <Box>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                <TextField
                    size="small"
                    label="User ID / Email"
                    value={filters.userId}
                    onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
                />
                <TextField
                    size="small"
                    label="Simulation Name"
                    value={filters.campaignId}
                    onChange={(e) => setFilters({ ...filters, campaignId: e.target.value })}
                />
                <TextField
                    size="small"
                    label="Date From"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={filters.dateFrom}
                    onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                />
                <TextField
                    size="small"
                    label="Date To"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={filters.dateTo}
                    onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Failed Status</InputLabel>
                    <Select
                        value={filters.dateFailed}
                        label="Failed Status"
                        onChange={(e) => setFilters({ ...filters, dateFailed: e.target.value })}
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="true">Failed Only</MenuItem>
                        <MenuItem value="false">Not Failed</MenuItem>
                    </Select>
                </FormControl>
                <Button variant="contained" onClick={() => handleSearch(0)}>
                    Search
                </Button>
            </Stack>

            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Typography color="error" sx={{ p: 2 }}>
                    Error loading data: {error.status || 'Unknown error'}
                </Typography>
            )}

            {data && (
                <>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Total results: {data.total}
                    </Typography>
                    <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600, minWidth: 60 }}>View</TableCell>
                                    {columns.map((col) => (
                                        <TableCell key={col.id} sx={{ fontWeight: 600, minWidth: col.width }}>
                                            {col.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.results && data.results.map((row, idx) => (
                                    <TableRow key={row.Id || idx} hover>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => setSelectedRow(row)}>
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>{row.Id}</TableCell>
                                        <TableCell>{formatDate(row.DateCreated)}</TableCell>
                                        <TableCell>{formatDate(row.DateUpdated)}</TableCell>
                                        <TableCell>{formatDate(row.DateSent)}</TableCell>
                                        <TableCell>{row.UserId || '-'}</TableCell>
                                        <TableCell>{row.CampaignId || '-'}</TableCell>
                                        <TableCell>{formatDate(row.DateFailed)}</TableCell>
                                        <TableCell>{formatDate(row.LandingPageClicked)}</TableCell>
                                        <TableCell>{row.LinkedUniversityCourseId || '-'}</TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem' }}>{row.UniqueIdentifier || '-'}</TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {row.UserAgent || '-'}
                                        </TableCell>
                                        <TableCell>{row.UserAgentIP || '-'}</TableCell>
                                    </TableRow>
                                ))}
                                {data.results && data.results.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={columns.length + 1} align="center">
                                            No results found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={data.total || 0}
                        page={page}
                        onPageChange={handlePageChange}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        rowsPerPageOptions={[25, 50, 100, 200]}
                    />
                </>
            )}

            <Dialog open={!!selectedRow} onClose={() => setSelectedRow(null)} maxWidth="md" fullWidth>
                <DialogTitle>Log Entry Details</DialogTitle>
                <DialogContent dividers>
                    {selectedRow && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {columns.map((col) => (
                                <Box key={col.id}>
                                    <Typography variant="caption" color="text.secondary">{col.label}</Typography>
                                    <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                                        {col.isDate
                                            ? formatDate(selectedRow[col.id])
                                            : (selectedRow[col.id] || '-')}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelectedRow(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            {!data && !isLoading && !error && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    Click Search to view most recent <Tooltip title="Records of phishing simulation campaigns that have been sent, including recipient details, campaign name, and delivery status." arrow placement="bottom"><span style={{ textDecoration: "underline dotted", cursor: "help", color: "inherit" }}>sent simulations logs</span></Tooltip> or enter search criteria to filter results.
                </Typography>
            )}
        </Box>
    );
};

export default SentCampaignsLog;
